import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CYAN = '#00F3FF';
const PURPLE = '#BC13FE';
const BG = '#030305';
const CARD_BG = '#0a0a12';
const BORDER = '#1a1a2e';

const styles = {
  page: {
    background: BG,
    minHeight: '100vh',
    color: '#e0e0e0',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    paddingTop: 100,
    paddingBottom: 80,
  },
  hero: {
    textAlign: 'center',
    padding: '60px 20px 40px',
    background: 'linear-gradient(180deg, #0a0018 0%, #030305 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  heroBadge: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${PURPLE}22, ${CYAN}22)`,
    border: `1px solid ${CYAN}55`,
    borderRadius: 30,
    padding: '6px 20px',
    fontSize: 12,
    letterSpacing: 3,
    color: CYAN,
    fontWeight: 700,
    marginBottom: 20,
    textTransform: 'uppercase',
  },
  heroTitle: {
    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
    fontWeight: 900,
    margin: '0 0 16px',
    background: `linear-gradient(135deg, #fff 0%, ${CYAN} 50%, ${PURPLE} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1.15,
  },
  heroSub: {
    color: '#888',
    fontSize: '1.1rem',
    maxWidth: 600,
    margin: '0 auto 30px',
    lineHeight: 1.7,
  },
  priceBadge: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${CYAN}33, ${PURPLE}33)`,
    border: `1px solid ${PURPLE}88`,
    borderRadius: 30,
    padding: '8px 24px',
    fontSize: 16,
    color: '#fff',
    fontWeight: 700,
    marginBottom: 10,
  },
  glowOrb: {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(80px)',
    opacity: 0.18,
    pointerEvents: 'none',
  },
  section: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '40px 20px',
  },
  sectionTitle: {
    fontSize: '1.6rem',
    fontWeight: 800,
    color: '#fff',
    marginBottom: 8,
    textAlign: 'center',
  },
  sectionLine: {
    width: 60,
    height: 3,
    background: `linear-gradient(90deg, ${CYAN}, ${PURPLE})`,
    margin: '0 auto 36px',
    borderRadius: 2,
  },
  cardsGrid: {
    display: 'flex',
    justifyContent: 'center',
    gap: 28,
    flexWrap: 'wrap',
  },
  card: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: '28px 28px 24px',
    width: '100%',
    maxWidth: 500,
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
    cursor: 'default',
    position: 'relative',
    overflow: 'hidden',
  },
  cardHoverStyle: {
    transform: 'translateY(-6px)',
    boxShadow: `0 20px 60px ${CYAN}22`,
    borderColor: `${CYAN}55`,
  },
  vramBadge: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${CYAN}33, ${PURPLE}33)`,
    border: `1px solid ${CYAN}66`,
    borderRadius: 20,
    padding: '4px 14px',
    fontSize: 12,
    fontWeight: 700,
    color: CYAN,
    letterSpacing: 1,
    marginBottom: 14,
    textTransform: 'uppercase',
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: 800,
    color: '#fff',
    marginBottom: 18,
  },
  specTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 20,
  },
  specRow: {
    borderBottom: `1px solid ${BORDER}`,
  },
  specKey: {
    padding: '8px 4px',
    color: '#666',
    fontSize: 13,
    width: '45%',
    verticalAlign: 'middle',
  },
  specVal: {
    padding: '8px 4px',
    color: '#ccc',
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'right',
    verticalAlign: 'middle',
  },
  price: {
    fontSize: '1.8rem',
    fontWeight: 900,
    color: CYAN,
    margin: '20px 0 6px',
    lineHeight: 1,
  },
  priceNote: {
    fontSize: 12,
    color: '#555',
    marginBottom: 22,
  },
  btnRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  waBtn: {
    flex: 1,
    minWidth: 130,
    padding: '11px 16px',
    background: 'linear-gradient(135deg, #25D366, #128C7E)',
    border: 'none',
    borderRadius: 10,
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'opacity 0.2s',
  },
  quoteBtn: {
    flex: 1,
    minWidth: 130,
    padding: '11px 16px',
    background: `linear-gradient(135deg, ${CYAN}22, ${PURPLE}22)`,
    border: `1px solid ${CYAN}55`,
    borderRadius: 10,
    color: CYAN,
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'background 0.2s',
  },
  gpuCapBox: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: '32px 32px 28px',
    marginTop: 20,
  },
  gpuSpecGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 20,
    marginTop: 24,
    marginBottom: 32,
  },
  gpuSpecItem: {
    background: '#0f0f1a',
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: '16px 18px',
    textAlign: 'center',
  },
  gpuSpecLabel: {
    fontSize: 11,
    color: '#555',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  gpuSpecValue: {
    fontSize: '1.2rem',
    fontWeight: 800,
    color: CYAN,
  },
  useCasesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginTop: 12,
  },
  useCaseCard: {
    background: '#0f0f1a',
    border: `1px solid ${PURPLE}33`,
    borderRadius: 12,
    padding: '18px 16px',
    textAlign: 'center',
  },
  useCaseEmoji: {
    fontSize: 28,
    marginBottom: 8,
    display: 'block',
  },
  useCaseName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#ccc',
  },
};

const openWA = (planName) => {
  const text = encodeURIComponent(`Hi, I'm interested in the ${planName} plan on IndiaCloud. Please share more details.`);
  window.open(`https://wa.me/919873385555?text=${text}`, '_blank');
};

function PlanCard({ plan }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ ...styles.card, ...(hovered ? styles.cardHoverStyle : {}) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ position: 'absolute', top: 0, right: 0, width: 120, height: 120,
        background: `radial-gradient(circle at top right, ${PURPLE}22, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={styles.vramBadge}>{plan.vramLabel}</div>
      <div style={styles.cardTitle}>{plan.name}</div>
      <table style={styles.specTable}>
        <tbody>
          {plan.specs.map(([k, v]) => (
            <tr key={k} style={styles.specRow}>
              <td style={styles.specKey}>{k}</td>
              <td style={styles.specVal}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={styles.price}>{plan.price}</div>
      <div style={styles.priceNote}>{plan.note}</div>
      <div style={styles.btnRow}>
        <button style={styles.waBtn} onClick={() => openWA(plan.name)}>💬 WhatsApp</button>
        <Link to="/contact" style={styles.quoteBtn}>Get Quote →</Link>
      </div>
    </div>
  );
}

const plan = {
  name: 'NVIDIA Tesla V100 32GB',
  vramLabel: '32GB V100 VRAM',
  price: '₹14,999/mo',
  note: 'SLA 99.95% • 24×7 Support',
  specs: [
    ['vCPU', '32 Cores'],
    ['RAM', '128 GB'],
    ['NVMe SSD', '500 GB'],
    ['GPU VRAM', '32 GB V100 HBM2'],
    ['SLA', '99.95%'],
    ['Support', '24×7'],
  ],
};

const gpuSpecs = [
  { label: 'GPU Model', value: 'Tesla V100 SXM2' },
  { label: 'CUDA Cores', value: '5,120' },
  { label: 'Tensor Cores', value: '640' },
  { label: 'Memory BW', value: '900 GB/s' },
  { label: 'FP32 Perf', value: '14 TFLOPS' },
  { label: 'FP16 Perf', value: '112 TFLOPS' },
  { label: 'NVLink', value: 'Yes' },
  { label: 'Form Factor', value: 'SXM2' },
];

const useCases = [
  { emoji: '🤖', name: 'AI/ML Inference' },
  { emoji: '🧠', name: 'Deep Learning Training' },
  { emoji: '🔬', name: 'Scientific Computing' },
  { emoji: '🎬', name: 'Video Rendering' },
];

export default function NvidiaV100() {
  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div style={{ ...styles.glowOrb, width: 400, height: 400, background: PURPLE, top: -100, left: '10%' }} />
        <div style={{ ...styles.glowOrb, width: 350, height: 350, background: CYAN, top: -80, right: '10%' }} />
        <div style={styles.heroBadge}>NVIDIA TESLA V100</div>
        <h1 style={styles.heroTitle}>NVIDIA Tesla V100<br />GPU Cloud</h1>
        <p style={styles.heroSub}>
          Harness the power of Volta architecture with HBM2 memory and NVLink for peak deep learning and HPC performance.
        </p>
        <div style={styles.priceBadge}>Starting at ₹14,999/mo</div>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Available Plan</h2>
        <div style={styles.sectionLine} />
        <div style={styles.cardsGrid}>
          <PlanCard plan={plan} />
        </div>
      </div>

      <div style={{ ...styles.section, paddingTop: 0 }}>
        <h2 style={styles.sectionTitle}>GPU Capabilities</h2>
        <div style={styles.sectionLine} />
        <div style={styles.gpuCapBox}>
          <div style={{ color: '#666', fontSize: 13, textAlign: 'center', marginBottom: 8 }}>
            NVIDIA Tesla V100 SXM2 — Volta Architecture
          </div>
          <div style={styles.gpuSpecGrid}>
            {gpuSpecs.map((s) => (
              <div key={s.label} style={styles.gpuSpecItem}>
                <div style={styles.gpuSpecLabel}>{s.label}</div>
                <div style={styles.gpuSpecValue}>{s.value}</div>
              </div>
            ))}
          </div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', marginBottom: 16, marginTop: 8 }}>Use Cases</h3>
          <div style={styles.useCasesGrid}>
            {useCases.map((u) => (
              <div key={u.name} style={styles.useCaseCard}>
                <span style={styles.useCaseEmoji}>{u.emoji}</span>
                <div style={styles.useCaseName}>{u.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
